import { z } from "zod";
import { AccountCharacterSummarySchema } from "@/lib/core/schemas/account-character";

export const LaunchSupportSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("supported") }),
  z.object({
    kind: z.literal("unsupported_version"),
    component: z.enum(["payload", "compiler", "plan"]),
  }),
  // Unreachable for schema-pinned account characters, carried for honesty
  // (the shared predicate is three-armed); UI renders it as
  // "Version unsupported" (plan D6).
  z.object({
    kind: z.literal("unsupported_record"),
    component: z.enum(["ruleset", "level"]),
  }),
]);
export type LaunchSupport = z.infer<typeof LaunchSupportSchema>;

export const LaunchCharacterSummarySchema =
  AccountCharacterSummarySchema.extend({ launchSupport: LaunchSupportSchema });
export type LaunchCharacterSummary = z.infer<
  typeof LaunchCharacterSummarySchema
>;

export const LaunchCampaignSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  rulesetId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  archivedAt: z.coerce.date().nullable(),
  assignedCharacters: z.array(
    z.object({
      characterId: z.string().uuid(),
      characterName: z.string(),
    })
  ),
});
export type LaunchCampaignSummary = z.infer<typeof LaunchCampaignSummarySchema>;

// Verified against source (2026-07-17): SessionContext.entitlement
// (lib/core/schemas/session.ts) is already the shipped tri-literal
// `"ADMIN" | "INVITEE" | "PUBLIC" | null` — ADMIN is a real entitlement
// value here (env allowlist/bypass/runner), not merely a scope. The
// verderem://account resource (lib/kernel/lifecycle.ts getUserAccount)
// passes `session.entitlement ?? null` straight through. No route-side
// derivation from scope is needed; this enum matches the wire value
// verbatim.
export const LaunchAccountSchema = z.object({
  userId: z.string(),
  email: z.string().nullable(),
  memberSince: z.coerce.date().nullable(),
  effectiveEntitlement: z.enum(["ADMIN", "INVITEE", "PUBLIC"]).nullable(),
});
export type LaunchAccount = z.infer<typeof LaunchAccountSchema>;

export const LaunchHubModelSchema = z.object({
  characters: z.array(LaunchCharacterSummarySchema),
  campaigns: z.array(LaunchCampaignSummarySchema),
  account: LaunchAccountSchema,
});
export type LaunchHubModel = z.infer<typeof LaunchHubModelSchema>;
